import { useForm } from "react-hook-form";
import { CHECKLIST } from "./consts";
import { planStatus } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useGetSafetyChecklistWithItemByProject, useGetSafetyCheckUpdates, useGetSafetyChecks } from "@/services";
import { toast } from "@/components";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setChecksData } from "@/redux/slices";

const useSafetyCompliance = () => {
  const {id} = useParams();
  const [openTabs, setOpenTabs] = useState<any>("1");
  const [isPhotoSliderOpen, setIsPhotoSliderOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<any[]>([]);
  const [activeCheckForSlider, setActiveCheckForSlider] = useState<any>(null);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [clickedType, setClickedType] = useState<"attachment" | "notes" | null>(null);
  const dispatch = useDispatch();
  const form = useForm<any>({
    defaultValues: {
      [CHECKLIST]: {},
    },
  });

  const {taskData, planData} = useSelector((state: RootState) => state.currentData)
  
  const isPlanApproved = useMemo(() => planData?.status == planStatus.approved.value, [planData]);

  const {data: safetyChecklist, isLoading: isLoadingChecklist} = useGetSafetyChecklistWithItemByProject({projectId: id as string})

  const {data: safetyChecks, isLoading: isLoadingChecks} = useGetSafetyChecks({
    projectId: id as string,
    taskId: taskData?.taskId
  })

  const { data: attachmentList, isLoading: isAttachmentsLoading } = useGetSafetyCheckUpdates({
    taskId: taskData?.taskId,
    projectId: id as string,
    checkId: activeCheckForSlider?.taskSafetyCheckId,
    queryParams: {
      enabled: !!activeCheckForSlider?.taskSafetyCheckId
    }
  })

  useEffect(() => {
    if (attachmentList?.data && activeCheckForSlider && clickedType) {
      if (clickedType === "attachment") {
        const allPhotos = attachmentList.data.reduce((acc: any[], update: any) => {
          const photosFromUpdate = update.attachments
            ?.filter((a: any) => a.presignedUrl)
            ?.map((a: any) => ({
              url: a.presignedUrl,
              description: a.fileDescription || update.description || "No description provided",
            }));
          
          return [...acc, ...(photosFromUpdate || [])];
        }, []);

        if (allPhotos.length > 0) {
          setSelectedPhotos(allPhotos);
          setIsPhotoSliderOpen(true);
        } else {
          toast({ message: "Not attachment found", type: "warn" });
        }
      } else if (clickedType === "notes") {
        setIsNotesDialogOpen(true);
      }
      setActiveCheckForSlider(null); 
      setClickedType(null);
    } else if (attachmentList && !attachmentList.data?.length && activeCheckForSlider && clickedType) {
       if (clickedType === "attachment") {
          toast({ message: "Not attachment found", type: "warn" });
       } else {
          setIsNotesDialogOpen(true);
       }
       setActiveCheckForSlider(null);
       setClickedType(null);
    }
  }, [attachmentList, activeCheckForSlider, clickedType]);

  useEffect(() => {
    if (safetyChecklist?.data &&  safetyChecks?.data){
      const groupedByChecklistId = safetyChecks?.data?.reduce(
        (acc: any, item: any, index: number) => {
          const key = item.taskSafetyChecklistId;

          const checklistData = safetyChecklist?.data?.find((val: any) => val?.taskSafetyChecklistId == key)

          if (!acc[key]) {
            acc[key] = {
              accordionId: (index + 1)?.toString(),
              taskSafetyChecklistId: key,
              ...checklistData,
              items: [],
            };
          }
      
          acc[key].items.push(item);

          const items = [...acc[key].items];

          acc[key].items = items?.map((val: any) => {
            return {
              ...val,
              ...checklistData.items.find((check: any) => check.taskSafetyChecklistItemId == val.taskSafetyChecklistItemId)
            }
          })
      
          return acc;
        },
        {}
      );
      form.setValue(CHECKLIST, groupedByChecklistId)
    }
  }, [safetyChecklist, safetyChecks]);
  
  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };

  const onAttachmentClick = (checksData: any) => {
    dispatch(setChecksData(checksData));
    setActiveCheckForSlider(checksData);
    setClickedType("attachment");
  }

  const onNotesClick = (checksData: any) => {
    dispatch(setChecksData(checksData));
    setActiveCheckForSlider(checksData);
    setClickedType("notes");
  }

  return {
    form,
    openTabs,
    onAccordionChange,
    isLoadingChecklist,
    isLoadingChecks,
    onAttachmentClick,
    onNotesClick,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    isNotesDialogOpen,
    setIsNotesDialogOpen,
    selectedPhotos,
    isAttachmentsLoading,
    isPlanApproved
  };
};

export default useSafetyCompliance;
